import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import type { EmailBodyFormat } from "../../types/emailBodyFormat";
import type { NotificationChannel } from "../NotificationChannelToggle/NotificationChannelToggle";
import { PremiumFeatureHint } from "../PremiumFeatureHint/PremiumFeatureHint";

/** Single control: SMS vs plain-text email vs HTML email (maps to `type` + `emailBodyFormat`). */
export type VisitDeliveryMode = "sms" | "plain_email" | "html_email";

export function visitDeliveryModeFromState(
  type: NotificationChannel,
  emailFmt: EmailBodyFormat
): VisitDeliveryMode {
  if (type === "sms") return "sms";
  return emailFmt === "html" ? "html_email" : "plain_email";
}

type ThreeProps = {
  variant: "three";
  value: VisitDeliveryMode;
  onChange: (mode: VisitDeliveryMode) => void;
  className?: string;
  /** When true, HTML email cannot be selected (Free / Early Adopter). */
  htmlLocked?: boolean;
};

/** Same three segments as `three`, but SMS is not available (email-only notifications). */
type ThreeSmsDisabledProps = {
  variant: "three_sms_disabled";
  value: EmailBodyFormat;
  onChange: (format: EmailBodyFormat) => void;
  className?: string;
  htmlLocked?: boolean;
};

export type NotificationDeliveryToggleProps = ThreeProps | ThreeSmsDisabledProps;

const threeModes: VisitDeliveryMode[] = ["sms", "plain_email", "html_email"];

function HtmlEmailOption({
  selected,
  locked,
  onSelect,
  className,
}: {
  selected: boolean;
  locked: boolean;
  onSelect: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const label = String(t("notifications.delivery.html_email"));
  const aria = String(t("notifications.delivery.html_email_aria"));
  const premiumTooltip = String(
    t("notifications.delivery.html_email_premium_tooltip", {
      storePlan: t("pricing.store.name"),
      brandPlan: t("pricing.brand.name"),
      professionalPlan: t("pricing.advancedStore.name"),
      subscriptionPage: t("benefits_page.pill.subscription"),
    })
  );

  return (
    <div className={cn("inline-flex items-stretch", className)}>
      <button
        type="button"
        disabled={locked}
        title={locked ? premiumTooltip : aria}
        aria-label={aria}
        aria-pressed={selected}
        onClick={() => {
          if (!locked) onSelect();
        }}
        className={cn(
          "px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-left",
          locked && "cursor-not-allowed bg-stone-50 text-stone-400",
          !locked && selected && "bg-stone-900 text-white",
          !locked && !selected && "bg-white text-stone-700 hover:bg-stone-100"
        )}
      >
        {label}
      </button>
      <span
        className={cn(
          "inline-flex items-center pr-2",
          locked && "bg-stone-50",
          !locked && selected && "bg-stone-900",
          !locked && !selected && "bg-white"
        )}
      >
        <PremiumFeatureHint tooltip={premiumTooltip} />
      </span>
    </div>
  );
}

export function NotificationDeliveryToggle(props: NotificationDeliveryToggleProps) {
  const { t } = useTranslation();
  const htmlLocked = !!props.htmlLocked;

  if (props.variant === "three_sms_disabled") {
    const fmt = props.value;
    const smsDisabledTitle = String(t("notifications.delivery.sms_disabled_title"));
    const smsDisabledAria = String(t("notifications.delivery.sms_disabled_aria"));

    return (
      <div
        className={cn(
          "inline-flex flex-wrap rounded-md border border-stone-200 bg-white overflow-hidden max-w-full",
          props.className
        )}
      >
        <button
          type="button"
          disabled
          title={smsDisabledTitle}
          aria-label={smsDisabledAria}
          className={cn(
            "px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium text-left border-r border-stone-200",
            "cursor-not-allowed bg-stone-50 text-stone-400"
          )}
        >
          {String(t("notifications.delivery.sms"))}
        </button>
        <button
          type="button"
          title={String(t("notifications.delivery.plain_email_aria"))}
          aria-label={String(t("notifications.delivery.plain_email_aria"))}
          aria-pressed={fmt === "plain"}
          onClick={() => props.onChange("plain")}
          className={cn(
            "px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium transition-colors text-left border-r border-stone-200",
            fmt === "plain" ? "bg-stone-900 text-white" : "bg-white text-stone-700 hover:bg-stone-100"
          )}
        >
          {String(t("notifications.delivery.plain_email"))}
        </button>
        <HtmlEmailOption
          selected={fmt === "html"}
          locked={htmlLocked}
          onSelect={() => props.onChange("html")}
        />
      </div>
    );
  }

  const label = (m: VisitDeliveryMode) => {
    if (m === "sms") return String(t("notifications.delivery.sms"));
    if (m === "plain_email") return String(t("notifications.delivery.plain_email"));
    return String(t("notifications.delivery.html_email"));
  };

  const aria = (m: VisitDeliveryMode) => {
    if (m === "sms") return String(t("notifications.delivery.sms_aria"));
    if (m === "plain_email") return String(t("notifications.delivery.plain_email_aria"));
    return String(t("notifications.delivery.html_email_aria"));
  };

  return (
    <div
      className={cn(
        "inline-flex flex-wrap rounded-md border border-stone-200 bg-white overflow-hidden max-w-full",
        props.className
      )}
    >
      {threeModes.map((mode, i) =>
        mode === "html_email" ? (
          <HtmlEmailOption
            key={mode}
            selected={props.value === mode}
            locked={htmlLocked}
            onSelect={() => props.onChange(mode)}
            className={i > 0 ? "border-l border-stone-200" : undefined}
          />
        ) : (
          <button
            key={mode}
            type="button"
            title={aria(mode)}
            aria-label={aria(mode)}
            aria-pressed={props.value === mode}
            onClick={() => props.onChange(mode)}
            className={cn(
              "px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium transition-colors text-left",
              i > 0 && "border-l border-stone-200",
              props.value === mode ? "bg-stone-900 text-white" : "bg-white text-stone-700 hover:bg-stone-100"
            )}
          >
            {label(mode)}
          </button>
        )
      )}
    </div>
  );
}
